import { useState } from 'react';
import {
  AANSPREEKVORMEN,
  MEET_LINK_MAX,
  SFEREN,
  normaliseerMeetLink,
  SUPPORTED_LANGUAGES,
  type Aanspreekvorm,
  type Sfeer,
} from '@tolk/shared';
import {
  ApiFout,
  logUitOpAlleApparaten,
  maakKamer,
  qrPad,
  uitloggen,
  type NieuweKamerAntwoord,
} from '../lib/api';
import Kader from '../components/Kader';
import Kamerlijst from '../components/Kamerlijst';
import { onthoudMijnKamer } from '../lib/mijnKamers';
import { bewaarQrAlsPng, QrDownloadFout } from '../lib/qrDownload';

/**
 * Hoe lang een gesprek bruikbaar blijft, gerekend vanaf het aanmaken.
 *
 * VASTE KEUZES EN GEEN VRIJ GETAL. De server accepteert 1 tot 168 uur en valt
 * bij alles daarbuiten stilzwijgend terug op de standaard - dan typ je 500 en
 * krijg je 4 zonder dat iemand het zegt. Met een lijst kan dat niet gebeuren.
 *
 * Vier uur is de standaard en blijft dat: genoeg voor een testmiddag. De langere
 * waarden zijn er voor een test die over meerdere dagen loopt, zodat je niet
 * elke keer een nieuwe link hoeft rond te sturen.
 */
const GELDIGHEID = [
  { uren: 1, label: '1 uur' },
  { uren: 4, label: '4 uur (standaard)' },
  { uren: 8, label: '8 uur' },
  { uren: 24, label: '1 dag' },
  { uren: 72, label: '3 dagen' },
  { uren: 168, label: '7 dagen' },
  // 0 betekent hier: geen einddatum. Zie shared/src/vast.ts.
  { uren: 0, label: 'Vast, zonder einddatum' },
] as const;

/**
 * Het plafond van een gesprek, in euro.
 *
 * VASTE KEUZES, om dezelfde reden als de geldigheid hierboven. De standaard
 * was jarenlang impliciet EUR 0,50 (BUDGET_ROOM_EUR op de server), omdat het
 * formulier nooit een bedrag meestuurde. Op 22 september 2026 liep een echt
 * gesprek daar halverwege tegenaan. Nu EUR 2: ruim genoeg voor een lang
 * gesprek, en loopt het toch vol, dan verhoog je hem in de kamer zelf.
 */
const BUDGETTEN = [
  { eur: 0.5, label: '€ 0,50' },
  { eur: 1, label: '€ 1' },
  { eur: 2, label: '€ 2 (standaard)' },
  { eur: 5, label: '€ 5' },
  { eur: 10, label: '€ 10' },
] as const;

/**
 * Kamer aanmaken. Voorloper van het adminpaneel uit stap 15; achter dezelfde
 * poort, die in productie dicht staat tot dat paneel er is.
 */
export default function HomePage({ opUitgelogd }: { opUitgelogd: () => void }) {
  const [langA, setLangA] = useState('nl-NL');
  const [langB, setLangB] = useState('hu-HU');
  const [label, setLabel] = useState('');
  const [ttlUren, setTtlUren] = useState(4);
  const [budgetEur, setBudgetEur] = useState(2);
  const [meetLink, setMeetLink] = useState('');
  const [aanspreekvorm, setAanspreekvorm] = useState<Aanspreekvorm>('automatisch');
  const [sfeer, setSfeer] = useState<Sfeer>('neutraal');
  const [stemDelen, setStemDelen] = useState(true);
  const [correctie, setCorrectie] = useState(true);
  const [alleenChat, setAlleenChat] = useState(false);
  const [metPin, setMetPin] = useState(false);
  const [debug, setDebug] = useState(false);
  const [bezig, setBezig] = useState(false);
  const [fout, setFout] = useState<string | null>(null);
  const [kamer, setKamer] = useState<NieuweKamerAntwoord | null>(null);
  const [overalBezig, setOveralBezig] = useState(false);
  const [overalMelding, setOveralMelding] = useState<string | null>(null);

  /**
   * Loopt op zodra er een kamer bij komt, zodat de lijst zichzelf ophaalt.
   *
   * Een teller en geen vlag: twee kamers achter elkaar aanmaken moet twee keer
   * verversen, en dezelfde vlag twee keer op `true` zetten is voor React geen
   * verandering.
   */
  const [verversSleutel, setVerversSleutel] = useState(0);

  // Leeg mag; iets wat geen Meet-link is, niet. De server toetst hetzelfde -
  // dit is alleen zodat je het ziet VOOR je op de knop drukt.
  const meetOngeldig = meetLink.trim() !== '' && normaliseerMeetLink(meetLink) === null;

  async function aanmaken(): Promise<void> {
    setBezig(true);
    setFout(null);
    try {
      // Onthouden dat DEZE browser de maker is. Daar hangt de kostenbalk aan;
      // zie lib/mijnKamers.ts voor waarom dat een briefje is en geen slot.
      const gemaakt = await maakKamer({
        label,
        ...(ttlUren === 0 ? { vast: true } : { ttlUren }),
        budgetEur,
        ...(meetLink.trim() !== '' ? { meetLink } : {}),
        langA,
        langB,
        aanspreekvorm,
        sfeer,
        stemDelen,
        correctie,
        alleenChat,
        metPin,
        debug,
      });
      onthoudMijnKamer(gemaakt.id);
      setKamer(gemaakt);
      setVerversSleutel((n) => n + 1);
    } catch (error) {
      setFout(error instanceof ApiFout ? error.message : 'Er ging iets mis.');
    } finally {
      setBezig(false);
    }
  }

  async function uitAanmelden(): Promise<void> {
    try {
      await uitloggen();
    } catch {
      // Lukt het wegsturen niet, dan ga je alsnog naar het inlogscherm. Het
      // cookie loopt hoe dan ook af, en blijven hangen op een scherm waar geen
      // knop meer werkt is erger.
    }
    opUitgelogd();
  }

  /**
   * De noodrem. Bevestiging eerst, want dit is niet terug te draaien: elk
   * ander apparaat moet daarna opnieuw het wachtwoord intypen.
   *
   * `window.confirm` en geen eigen venster. Dat is lelijker, maar het is de
   * enige bevestiging die op elke telefoon werkt zonder dat er iets nieuws
   * getest hoeft te worden - en dit is precies de knop waarbij een
   * half-werkende bevestiging het ergst is.
   */
  async function overalUitloggen(): Promise<void> {
    const zeker = window.confirm(
      'Alle andere apparaten uitloggen?\n\n' +
        'Elk apparaat waarop je nu ingelogd bent - een oude telefoon, een ' +
        'browser bij iemand anders - staat hierna buiten en moet opnieuw ' +
        'inloggen. Dit apparaat blijft ingelogd.\n\n' +
        'Lopende gesprekken gaan gewoon door: gasten hebben hier geen last van.',
    );
    if (!zeker) return;

    setOveralBezig(true);
    setOveralMelding(null);
    try {
      await logUitOpAlleApparaten();
      setOveralMelding('Gelukt. Alle andere apparaten staan buiten.');
    } catch (error) {
      // Een 401 hier betekent dat DIT apparaat ook al buiten stond. Dan is het
      // inlogscherm het eerlijke antwoord, geen foutmelding op een scherm waar
      // toch niets meer werkt.
      if (error instanceof ApiFout && error.code === 'niet_ingelogd') {
        opUitgelogd();
        return;
      }
      setOveralMelding('Niet gelukt. Probeer het nog een keer.');
    } finally {
      setOveralBezig(false);
    }
  }

  if (kamer !== null) {
    return <KamerKlaar kamer={kamer} opnieuw={() => setKamer(null)} />;
  }

  return (
    <Kader
      titel="Nieuw tolkgesprek"
      onderschrift="Kies de twee talen. Je krijgt een QR-code die je deelt met de ander."
      taalcode="nl"
    >
      <div className="space-y-4">
        {/*
          BOVEN HET FORMULIER, want terugkomen bij een lopend gesprek is
          dringender dan een nieuw gesprek beginnen: je zoekt hem juist omdat je
          de link kwijt bent. Zijn er geen lopende gesprekken, dan toont dit
          blok niets en begint het scherm gewoon bij de naam.
        */}
        <Kamerlijst verversSleutel={verversSleutel} />

        <Veld label="Naam van het gesprek (mag leeg)">
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Bijvoorbeeld: gesprek met Anna"
            className="w-full p-2.5 bg-slate-50 border-2 border-slate-250 rounded-xl text-sm
                       text-slate-800 placeholder-slate-405 focus:outline-none focus:border-gio-blue
                       focus:bg-white transition-colors duration-155"
          />
        </Veld>

        <Veld label="Hoe lang blijft het gesprek bruikbaar">
          <select
            value={ttlUren}
            onChange={(e) => setTtlUren(Number(e.target.value))}
            className="w-full p-2.5 bg-slate-50 border-2 border-slate-250 rounded-xl text-sm
                       text-slate-800 focus:outline-none focus:border-gio-blue focus:bg-white
                       transition-colors duration-155 cursor-pointer"
          >
            {GELDIGHEID.map((keuze) => (
              <option key={keuze.uren} value={keuze.uren}>
                {keuze.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-slate-450 leading-relaxed">
            {ttlUren === 0
              ? 'Blijft open tot je hem zelf sluit met de prullenbak. Berichten blijven 30 dagen bewaard en gaan daarna weg. Zet er een pincode op: de link werkt anders voor altijd, ook als hij wordt doorgestuurd.'
              : 'Gerekend vanaf nu. Verlengen of vastzetten kan in het gesprek zelf, onder Kosten. De exacte eindtijd staat op het voorportaal, bij "Verloopt om".'}
          </p>
        </Veld>

        <Veld label="Maximaal bedrag voor dit gesprek">
          <select
            value={budgetEur}
            onChange={(e) => setBudgetEur(Number(e.target.value))}
            className="w-full p-2.5 bg-slate-50 border-2 border-slate-250 rounded-xl text-sm
                       text-slate-800 focus:outline-none focus:border-gio-blue focus:bg-white
                       transition-colors duration-155 cursor-pointer"
          >
            {BUDGETTEN.map((keuze) => (
              <option key={keuze.eur} value={keuze.eur}>
                {keuze.label}
              </option>
            ))}
          </select>
          <p className="mt-1 text-[11px] text-slate-450 leading-relaxed">
            Bij 80% zie je een waarschuwing. Ophogen kan tijdens het gesprek, met dezelfde link.
          </p>
        </Veld>

        <Veld label="Google Meet-link (mag leeg)">
          <input
            type="url"
            inputMode="url"
            value={meetLink}
            maxLength={MEET_LINK_MAX}
            onChange={(e) => setMeetLink(e.target.value)}
            placeholder="https://meet.google.com/abc-defg-hij"
            className="w-full p-2.5 bg-slate-50 border-2 border-slate-250 rounded-xl text-sm
                       text-slate-800 placeholder-slate-405 focus:outline-none focus:border-gio-blue
                       focus:bg-white transition-colors duration-155"
          />
          {meetOngeldig ? (
            <p className="mt-1 text-[11px] text-gio-danger leading-relaxed">
              Dat is geen Google Meet-link. Alleen links van meet.google.com.
            </p>
          ) : (
            <p className="mt-1 text-[11px] text-slate-450 leading-relaxed">
              Wordt bewaard bij het gesprek. Je gast ziet een knop om mee te doen.
            </p>
          )}
        </Veld>

        <div className="grid grid-cols-2 gap-3">
          <Veld label="Spreker 1">
            <TaalKiezer waarde={langA} opWijzig={setLangA} />
          </Veld>
          <Veld label="Spreker 2">
            <TaalKiezer waarde={langB} opWijzig={setLangB} />
          </Veld>
        </div>

        {langA === langB && (
          <p className="text-xs text-gio-danger">
            Twee keer dezelfde taal is geen tolkgesprek. Kies er een andere.
          </p>
        )}

        <div className="border-t-2 border-slate-150 pt-4">
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-450 mb-2">
            Toon van de vertaling
          </p>
          <div className="grid grid-cols-2 gap-3">
            <select
              value={aanspreekvorm}
              onChange={(e) => setAanspreekvorm(e.target.value as Aanspreekvorm)}
              className="w-full p-2.5 bg-slate-50 border-2 border-slate-250 rounded-xl text-sm
                         text-slate-800 cursor-pointer focus:outline-none focus:border-gio-blue
                         transition-colors duration-155"
            >
              {AANSPREEKVORMEN.map((a) => (
                <option key={a.waarde} value={a.waarde}>
                  {a.label}
                </option>
              ))}
            </select>
            <select
              value={sfeer}
              onChange={(e) => setSfeer(e.target.value as Sfeer)}
              className="w-full p-2.5 bg-slate-50 border-2 border-slate-250 rounded-xl text-sm
                         text-slate-800 cursor-pointer focus:outline-none focus:border-gio-blue
                         transition-colors duration-155"
            >
              {SFEREN.map((s) => (
                <option key={s.waarde} value={s.waarde}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
          <p className="text-[11px] text-slate-450 mt-1.5 leading-relaxed">
            {AANSPREEKVORMEN.find((a) => a.waarde === aanspreekvorm)?.uitleg} ·{' '}
            {SFEREN.find((s) => s.waarde === sfeer)?.uitleg}. Zonder sturing kiest het model
            bijna altijd de formele vorm.
          </p>
        </div>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={alleenChat}
            onChange={(e) => setAlleenChat(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-gio-blue cursor-pointer"
          />
          <span className="text-xs text-slate-650 leading-relaxed">
            <b className="text-gio-dark">Alleen chat, geen spraak.</b> Jullie typen; de vertaling
            en de originele tekst staan in beeld. Geen microfoon en niets wordt voorgelezen. Sneller,
            en goedkoper. Handig naast een videogesprek. Tijdens het gesprek nog om te zetten.
          </span>
        </label>

        {/* Bij alleen chat wordt er niets opgenomen, dus ook geen stem om mee te sturen. */}
        <label
          className={`flex items-start gap-2.5 cursor-pointer ${alleenChat ? 'hidden' : ''}`}
        >
          <input
            type="checkbox"
            checked={stemDelen}
            onChange={(e) => setStemDelen(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-gio-blue cursor-pointer"
          />
          <span className="text-xs text-slate-650 leading-relaxed">
            <b className="text-gio-dark">Stuur ook je eigen stem mee.</b> De ander hoort hoe jij
            het zegt, naast de vertaling. Handig om een taal te leren. Zet dit uit bij een
            gevoelig gesprek: wie de opname ontvangt, kan hem bewaren.
          </span>
        </label>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={correctie}
            onChange={(e) => setCorrectie(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-gio-blue cursor-pointer"
          />
          <span className="text-xs text-slate-650 leading-relaxed">
            <b className="text-gio-dark">Verbeter spelfouten in je eigen tekst.</b> Zet dit uit
            om te zien wat het model werkelijk verstond. Staat er een rare zin, dan weet je zo of
            hij verkeerd verstaan is of netjes verstaan en daarna &ldquo;verbeterd&rdquo;.
          </span>
        </label>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={metPin}
            onChange={(e) => setMetPin(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-gio-orange cursor-pointer"
          />
          <span className="text-xs text-slate-650 leading-relaxed">
            <b className="text-gio-dark">Pincode erbij.</b> Geef die mondeling door, niet in
            hetzelfde bericht als de link. Beschermt tegen een doorgestuurde link.
          </span>
        </label>

        <label className="flex items-start gap-2.5 cursor-pointer">
          <input
            type="checkbox"
            checked={debug}
            onChange={(e) => setDebug(e.target.checked)}
            className="mt-0.5 w-4 h-4 accent-gio-blue cursor-pointer"
          />
          <span className="text-xs text-slate-650 leading-relaxed">
            <b className="text-gio-dark">Toon de kosten per bericht.</b> Onder elke vertaling
            komt een regeltje met de duur, het aantal tokens en wat die ene zin kostte. Voor
            jezelf, niet voor de gast: de ander ziet het ook.
          </span>
        </label>

        {fout !== null && (
          <p className="text-xs text-gio-danger bg-gio-danger/5 border-2 border-gio-danger/20 rounded-xl px-3 py-2">
            {fout}
          </p>
        )}

        <button
          type="button"
          onClick={() => void aanmaken()}
          disabled={bezig || langA === langB || meetOngeldig}
          className="w-full py-3 rounded-xl bg-gio-orange hover:bg-gio-orange-deep text-white
                     font-bold text-sm transition-colors duration-155
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {bezig ? 'Bezig…' : 'Maak de kamer'}
        </button>

        {/* Onopvallend, maar het moet er zijn: een deur zonder uitgang is geen
            deur, en zonder deze knop is de logout-route dode code. */}
        <button
          type="button"
          onClick={() => void uitAanmelden()}
          className="w-full text-[11px] text-slate-450 hover:text-gio-dark
                     transition-colors duration-155 underline"
        >
          Uitloggen
        </button>

        {/* DE NOODREM, en die staat er bewust onopvallender bij dan "Uitloggen"
            en in het rood. Rood is in dit project voorbehouden aan ingrepen die
            je niet terugdraait, en dit is er een: hij raakt apparaten die je op
            dit moment niet in je hand hebt. */}
        <button
          type="button"
          onClick={() => void overalUitloggen()}
          disabled={overalBezig}
          className="w-full text-[11px] text-slate-450 hover:text-gio-danger
                     transition-colors duration-155 underline
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {overalBezig ? 'Bezig…' : 'Log alle andere apparaten uit'}
        </button>

        {overalMelding !== null && (
          <p className="text-[11px] text-center text-slate-450">{overalMelding}</p>
        )}
      </div>
    </Kader>
  );
}

function KamerKlaar({ kamer, opnieuw }: { kamer: NieuweKamerAntwoord; opnieuw: () => void }) {
  const [gekopieerd, setGekopieerd] = useState(false);
  const [bezigMetQr, setBezigMetQr] = useState(false);
  const [qrFout, setQrFout] = useState<string | null>(null);

  async function downloadQr(): Promise<void> {
    setBezigMetQr(true);
    setQrFout(null);
    try {
      await bewaarQrAlsPng(qrPad(kamer.id), kamer.id);
    } catch (fout) {
      setQrFout(
        fout instanceof QrDownloadFout ? fout.message : 'Opslaan is niet gelukt. Probeer opnieuw.',
      );
    } finally {
      setBezigMetQr(false);
    }
  }

  async function kopieer(): Promise<void> {
    try {
      await navigator.clipboard.writeText(kamer.link);
      setGekopieerd(true);
      window.setTimeout(() => setGekopieerd(false), 2000);
    } catch {
      // Zonder toestemming voor het klembord blijft de link zichtbaar en kun
      // je hem met de hand selecteren. Geen melding nodig.
    }
  }

  return (
    <Kader
      titel="De kamer staat klaar"
      onderschrift="Laat de ander deze code scannen."
      taalcode="nl"
    >
      <div className="flex flex-col items-center gap-2.5 mb-5">
        <img
          src={qrPad(kamer.id)}
          alt="QR-code voor deze kamer"
          className="w-52 h-52 border-2 border-slate-250 rounded-xl bg-white p-2"
        />
        <button
          type="button"
          onClick={() => void downloadQr()}
          disabled={bezigMetQr}
          className="px-3.5 py-2 rounded-xl border-2 border-slate-250 bg-slate-50 hover:bg-slate-150
                     text-xs font-bold text-slate-650 transition-colors duration-155
                     disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {bezigMetQr ? 'Bezig\u2026' : 'Bewaar de QR-code'}
        </button>
        {qrFout !== null && (
          <p className="text-xs text-gio-danger text-center px-4">{qrFout}</p>
        )}
      </div>

      {kamer.pin !== null && (
        <div className="mb-4 text-center bg-gio-orange/10 border-2 border-gio-orange/25 rounded-xl py-3">
          <p className="text-[10px] uppercase tracking-widest text-slate-550 font-bold mb-1">
            Pincode
          </p>
          <p className="font-mono text-3xl font-bold text-gio-dark tracking-[0.3em]">{kamer.pin}</p>
          <p className="text-[11px] text-slate-550 mt-1.5 px-4 leading-relaxed">
            Deze code is nu eenmalig te zien. Geef hem mondeling door.
          </p>
        </div>
      )}

      <div className="mb-4">
        <p className="text-[10px] uppercase tracking-widest text-slate-450 font-bold mb-1.5">
          Of stuur de link
        </p>
        <div className="flex gap-2">
          <input
            readOnly
            value={kamer.link}
            onFocus={(e) => e.currentTarget.select()}
            className="flex-1 min-w-0 p-2 bg-slate-50 border-2 border-slate-250 rounded-xl
                       font-mono text-[11px] text-slate-650"
          />
          <button
            type="button"
            onClick={() => void kopieer()}
            className="px-3 rounded-xl border-2 border-slate-250 bg-slate-50 hover:bg-slate-150
                       text-xs font-bold text-slate-650 transition-colors duration-155"
          >
            {gekopieerd ? 'Gekopieerd' : 'Kopieer'}
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={opnieuw}
        className="w-full py-2.5 rounded-xl border-2 border-slate-250 text-xs font-bold
                   text-slate-550 hover:text-gio-dark hover:border-slate-350
                   transition-colors duration-155"
      >
        Nog een kamer maken
      </button>
    </Kader>
  );
}

function Veld({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <span className="block text-[10px] font-bold uppercase tracking-wider text-slate-450 mb-1.5">
        {label}
      </span>
      {children}
    </div>
  );
}

function TaalKiezer({ waarde, opWijzig }: { waarde: string; opWijzig: (v: string) => void }) {
  return (
    <select
      value={waarde}
      onChange={(e) => opWijzig(e.target.value)}
      className="w-full p-2.5 bg-slate-50 border-2 border-slate-250 rounded-xl text-sm
                 text-slate-800 cursor-pointer focus:outline-none focus:border-gio-blue
                 transition-colors duration-155"
    >
      {SUPPORTED_LANGUAGES.map((taal) => (
        <option key={taal.code} value={taal.code}>
          {taal.flag} {taal.label}
        </option>
      ))}
    </select>
  );
}
